import React, { useEffect, useRef, useState } from "react";
import { format, isValid, parse } from "date-fns";
import { parseTwoDigitYear } from "../../utils/dateFormatter";

// Typed date format (e.g. 10/08/2026)
export const DATE_INPUT_FORMAT = "MM/dd/yyyy";
// Shown once a date is picked/typed (e.g. Oct/08/2026)
export const DATE_DISPLAY_FORMAT = "MMM/dd/yyyy";
// DatePicker dateFormat that shows Oct/08/2026 and also parses a typed
// 10/08/2026. Use it together with strictParsing.
export const DATE_PICKER_FORMATS = [DATE_DISPLAY_FORMAT, DATE_INPUT_FORMAT];

// Format guide shown behind the typed text until the date is complete:
// "" -> MM/DD/YYYY, "1" -> 1M/DD/YYYY, "10/1" -> 10/1D/YYYY
const FORMAT_GUIDE = "MM/DD/YYYY";

const COMPLETE_DATE = /^\d{2}\/\d{2}\/\d{4}$/;
const DISPLAYED_DATE = /^[A-Za-z]{3}\/\d{2}\/\d{4}$/;

// Keeps digits only and adds the slashes. Returns null when the text isn't a
// valid prefix of MM/dd/yyyy (the input then keeps its previous text).
const maskTypedDate = (raw) => {
  const next = raw
    .replace(/[^\d/]/g, "")
    .replace(/^(\d{2})(\d)/, "$1/$2")
    .replace(/^(\d{2}\/\d{2})(\d)/, "$1/$2");

  return /^(\d{0,2}|\d{2}\/\d{0,2}|\d{2}\/\d{2}\/\d{0,4})$/.test(next)
    ? next
    : null;
};

// "Oct/10/2026" -> "10/10/2026" so a shown date can be edited as digits
const toTypedText = (text) => {
  if (!DISPLAYED_DATE.test(text)) return text;
  const date = parse(text, DATE_DISPLAY_FORMAT, new Date());
  return isValid(date) ? format(date, DATE_INPUT_FORMAT) : text;
};

// Overlay for the format guide. Place it right after the <input>, inside a
// position-relative wrapper, and give it the input's className/style so the
// padding and font line up. The typed part is hidden so only the remaining
// guide letters show after the text. Renders nothing once the date is complete.
export const DateFormatGuide = ({ text = "", className, style }) => {
  if (text.length >= FORMAT_GUIDE.length) return null;

  return (
    <div
      aria-hidden="true"
      className={`${className} position-absolute top-0 start-0 h-100 d-flex align-items-center text-nowrap overflow-hidden`}
      style={{
        ...style,
        pointerEvents: "none",
        borderColor: "transparent",
        backgroundColor: "transparent",
        backgroundImage: "none",
        boxShadow: "none",
      }}
    >
      <span style={{ visibility: "hidden", whiteSpace: "pre" }}>{text}</span>
      <span className="text-secondary">{FORMAT_GUIDE.slice(text.length)}</span>
    </div>
  );
};

// Custom input for react-datepicker that lets the user type the date:
//  - typed as MM/dd/yyyy; only digits are kept and the slashes are added
//    automatically, anything else is rejected
//  - the MM/DD/YYYY guide stays visible while typing (only the parts not typed
//    yet are shown) and disappears once the date is complete
//  - only a complete date (or a cleared field) is passed to the DatePicker, so
//    the page's onChange / cutoff validation never runs on a half-typed date
//  - a 2-digit year (10/10/26) is completed to 10/10/2026 on Enter, Tab or
//    when leaving the field
//  - a shown date (Oct/10/2026) switches to 10/10/2026 on focus and is
//    selected, so typing replaces it; incomplete text snaps back on blur
//  - placeholder (optional) is shown while not focused, e.g. "Select Date";
//    without one the guide is shown instead
//
// Usage:
//   <DatePicker
//     selected={date}
//     onChange={handleChange}
//     customInput={<TypedDateInput />}
//     dateFormat={DATE_PICKER_FORMATS}
//     strictParsing
//     autoComplete="off"
//   />
//
// onTextChange (optional) is called with the masked text on every keystroke.
const TypedDateInput = React.forwardRef(
  (
    {
      value,
      onClick,
      onChange,
      onBlur,
      onFocus,
      onKeyDown,
      disabled,
      required,
      className,
      style,
      placeholder,
      autoFocus,
      onTextChange,
    },
    ref,
  ) => {
    const inputRef = useRef(null);
    const [localValue, setLocalValue] = useState(value || "");
    const [isFocused, setIsFocused] = useState(false);
    const inputClassName = className || "form-control custom-form-height";

    // Sync with react-datepicker when it confirms a date (or clears it)
    useEffect(() => {
      setLocalValue(value || "");
    }, [value]);

    // Keep our own ref and still hand the input to react-datepicker
    const setRefs = (node) => {
      inputRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    };

    // Let react-datepicker parse the text; it calls the page's onChange
    const sendToPicker = (e, text) => {
      e.target.value = text;
      if (onChange) onChange(e);
    };

    // Returns true when the text was completed and sent to the picker
    const completeTwoDigitYear = (e) => {
      const date = parseTwoDigitYear(e.target.value);
      if (!date) return false;

      const text = format(date, DATE_INPUT_FORMAT);
      setLocalValue(text);
      sendToPicker(e, text);
      return true;
    };

    const handleChange = (e) => {
      const next = maskTypedDate(e?.target?.value || "");
      if (next === null) return;

      setLocalValue(next);
      if (onTextChange) onTextChange(next);
      if (next === "" || COMPLETE_DATE.test(next)) sendToPicker(e, next);
    };

    return (
      <div className="position-relative">
        <input
          type="text"
          className={inputClassName}
          style={{ cursor: disabled ? undefined : "text", ...style }}
          onClick={onClick}
          onChange={handleChange}
          onFocus={(e) => {
            setIsFocused(true);
            setLocalValue(toTypedText(e.target.value));
            setTimeout(() => inputRef.current?.select());
            if (onFocus) onFocus(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            completeTwoDigitYear(e);
            // Incomplete / rejected text: show the picker's date again
            setLocalValue(value || "");
            if (onBlur) onBlur(e);
          }}
          onKeyDown={(e) => {
            const completed =
              (e.key === "Enter" || e.key === "Tab") && completeTwoDigitYear(e);

            // The picker hasn't taken the completed date yet; handing it Enter
            // now would select its highlighted day instead (e.g. today). Stop
            // the form submit here and pass the key on once the date is in.
            if (completed && e.key === "Enter") {
              e.preventDefault();
              setTimeout(() => onKeyDown && onKeyDown(e));
              return;
            }
            if (onKeyDown) onKeyDown(e);
          }}
          value={localValue}
          ref={setRefs}
          placeholder={isFocused ? "" : placeholder}
          disabled={disabled}
          required={required}
          autoFocus={autoFocus}
        />
        {(isFocused || !placeholder) && (
          <DateFormatGuide
            text={localValue}
            className={inputClassName}
            style={style}
          />
        )}
      </div>
    );
  },
);

TypedDateInput.displayName = "TypedDateInput";

export default TypedDateInput;
